/**
 * TenantContext.tsx
 *
 * Manages multi-clinic session state for the authenticated user.
 *
 * Key concepts:
 * - `accessibleClinics`: all clinics the user has access to (from /api/v1/auth/my-clinics).
 * - `activeClinicId`: the clinic currently selected for data operations.
 * - `switchClinic(id)`: changes the active clinic; persists to sessionStorage.
 * - Every API request automatically includes X-Active-Clinic-Id header (see api/client.ts).
 *
 * Backward compatibility:
 * - Single-clinic users have exactly one accessibleClinic.
 * - `isMultiClinic` is false for them — no branch switcher UI is shown.
 * - All existing code that reads `clinicId` from AuthContext is unaffected;
 *   it reads the primary clinic, which equals the active clinic for single-clinic users.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { secureStorage } from '../lib/secureStorage';
import type { AccessibleClinic } from '../lib/api/modules/auth';
import { useAuth } from './AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantContextValue {
  /** All clinics this user has active access to. Empty while loading. */
  accessibleClinics: AccessibleClinic[];

  /** The clinicId currently active for API requests. */
  activeClinicId: string | null;

  /** The currently active clinic object (convenience). */
  activeClinic: AccessibleClinic | null;

  /** True if the user has access to more than one clinic. */
  isMultiClinic: boolean;

  /** True while loading clinic list from the server. */
  loadingClinics: boolean;

  /**
   * Switches the active clinic.
   * Validates access server-side before committing.
   * Returns true on success, false if access was denied.
   */
  switchClinic: (clinicId: string) => Promise<boolean>;

  /**
   * Loads (or reloads) the list of accessible clinics.
   * Call after login, or when user_clinic_access changes.
   */
  loadAccessibleClinics: () => Promise<void>;

  /** Clears all tenant state (called on logout). */
  clearTenantState: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [accessibleClinics, setAccessibleClinics] = useState<AccessibleClinic[]>([]);
  const [activeClinicId, setActiveClinicId] = useState<string | null>(
    secureStorage.getActiveClinicId()
  );
  const [loadingClinics, setLoadingClinics] = useState(false);

  const activeClinic = accessibleClinics.find(c => c.clinicId === activeClinicId) ?? null;
  const isMultiClinic = accessibleClinics.length > 1;

  // Auto-load clinics when the user authenticates; clear when they log out.
  useEffect(() => {
    if (!authLoading && user) {
      loadAccessibleClinics();
    } else if (!authLoading && !user) {
      clearTenantState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authLoading]);

  // Load accessible clinics from backend
  const loadAccessibleClinics = useCallback(async () => {
    setLoadingClinics(true);
    try {
      const response = await api.auth.getMyClinics();
      const clinics: AccessibleClinic[] = (response.data as any) ?? [];
      setAccessibleClinics(clinics);

      // Auto-select: if no activeClinicId stored, or stored one is no longer accessible, pick primary
      const storedId = secureStorage.getActiveClinicId();
      const storedStillValid = storedId && clinics.some(c => c.clinicId === storedId);

      if (!storedStillValid) {
        const primary = clinics.find(c => c.isPrimary) ?? clinics[0] ?? null;
        if (primary) {
          secureStorage.setActiveClinicId(primary.clinicId);
          setActiveClinicId(primary.clinicId);
        }
      } else {
        // The login stored this account's clinic; keep the state in step with it (and with the header).
        setActiveClinicId(storedId);
      }
    } catch {
      // Non-fatal: single-clinic flow still works via JWT clinicId fallback
    } finally {
      setLoadingClinics(false);
    }
  }, []);

  // Switch to a different clinic
  const switchClinic = useCallback(async (clinicId: string): Promise<boolean> => {
    // Quick local check first (avoids unnecessary server round-trip)
    const hasLocal = accessibleClinics.some(c => c.clinicId === clinicId);
    if (!hasLocal) return false;

    try {
      const response = await api.auth.validateClinicAccess(clinicId);
      const result = (response.data as any);
      if (!result?.allowed) return false;

      secureStorage.setActiveClinicId(clinicId);
      setActiveClinicId(clinicId);
      return true;
    } catch {
      return false;
    }
  }, [accessibleClinics]);

  const clearTenantState = useCallback(() => {
    setAccessibleClinics([]);
    setActiveClinicId(null);
    secureStorage.removeActiveClinicId();
  }, []);

  const value: TenantContextValue = {
    accessibleClinics,
    activeClinicId,
    activeClinic,
    isMultiClinic,
    loadingClinics,
    switchClinic,
    loadAccessibleClinics,
    clearTenantState,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside <TenantProvider>');
  }
  return ctx;
}
